// src/hooks/useInstagramIAB.js
// Детект in-app браузеров: Instagram/FB + Telegram WebView/WebApp
//
// Цели:
// 1) Надёжно отличать Instagram/FB IAB и Telegram окружение.
// 2) Не ломать существующий API хука (isInstagramIAB, isTelegramEnv, isInApp).
// 3) Дать простой ручной оверрайд для тестов без DevTools внутри IAB.
//    Добавь к URL: ?force_env=ig | tg | wa | browser

import { useEffect, useState } from "react";

function getForcedEnv() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search || "");
    const raw = params.get("force_env") || params.get("forceEnv");
    if (!raw) return null;
    const v = String(raw).toLowerCase();
    if (v === "ig" || v === "instagram") return "ig";
    if (v === "tg" || v === "telegram") return "tg";
    if (v === "wa" || v === "whatsapp") return "wa";
    if (v === "browser" || v === "web") return "browser";
    return null;
  } catch {
    return null;
  }
}

export default function useInstagramIAB() {
  const [env, setEnv] = useState({
    isInstagramIAB: false,
    isTelegramEnv: false,
    isInApp: false,
    // дополнительные поля для дебага (не обязательны для использования)
    isInstagramUA: false,
    isFacebookIABUA: false,
    isIGRef: false,
    isFBRef: false,
    isTelegramUA: false,
    hasTelegramWebApp: false,
    isWhatsAppUA: false,
    isWhatsAppRef: false,
    isWhatsAppEnv: false,
    forcedEnv: null,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const ua = (navigator && navigator.userAgent) ? navigator.userAgent : "";
    const ref = (document && document.referrer) ? document.referrer : "";

    // --- Manual override for testing ---
    const forcedEnv = getForcedEnv();

    // Instagram / Facebook in-app
    const isInstagramUA = /Instagram/i.test(ua);
    const isFacebookIABUA = /fb_iab|FBAV|FBAN/i.test(ua);
    const isIGRef = /instagram\.com|l\.instagram\.com/i.test(ref);
    const isFBRef = /facebook\.com|m\.facebook\.com/i.test(ref);

    // Telegram in-app:
    //  1) Обычный Telegram WebView → в UA есть "Telegram"
    //  2) Mini App → непустой initData.
    // Просто наличие Telegram SDK не подходит как сигнал:
    // telegram-web-app.js подключён глобально и может существовать в обычном браузере.
    const isTelegramUA = /Telegram/i.test(ua);
    const hasTelegramWebApp = Boolean(window.Telegram?.WebApp?.initData);
    const isWhatsAppUA = /WhatsApp/i.test(ua);
    const isWhatsAppRef = /whatsapp/i.test(ref);

    // Детект «чистого» Safari на iOS:
    //  - есть Safari
    //  - НЕТ признаков Chrome/Firefox/Edge/Instagram/Facebook/Telegram
    const isIOS = /iP(ad|hone|od)/i.test(ua);
    const isSafari =
      isIOS &&
      /Safari/i.test(ua) &&
      !/CriOS|FxiOS|OPR|EdgiOS|Instagram|FBAN|FBAV|Telegram/i.test(ua);

    // Базовый детект in-app по UA и наличию Telegram WebApp
    let isInstagramIAB = isInstagramUA || isFacebookIABUA || isIGRef || isFBRef;
    let isTelegramEnv = Boolean(isTelegramUA || hasTelegramWebApp);
    let isWhatsAppEnv = Boolean(isWhatsAppUA || isWhatsAppRef);

    // Если это обычный Safari и нет принудительного режима через ?force_env,
    // всегда считаем его нормальным браузером, даже если referrer указывает на Instagram/Facebook.
    if (isSafari && !forcedEnv) {
      isInstagramIAB = false;
      isTelegramEnv = false;
      isWhatsAppEnv = false;
    }

    if (forcedEnv === "ig") {
      isInstagramIAB = true;
      isTelegramEnv = false;
      isWhatsAppEnv = false;
    } else if (forcedEnv === "tg") {
      isInstagramIAB = false;
      isTelegramEnv = true;
      isWhatsAppEnv = false;
    } else if (forcedEnv === "wa") {
      isInstagramIAB = false;
      isTelegramEnv = false;
      isWhatsAppEnv = true;
    } else if (forcedEnv === "browser") {
      isInstagramIAB = false;
      isTelegramEnv = false;
      isWhatsAppEnv = false;
    }

    const isInApp = isInstagramIAB || isTelegramEnv || isWhatsAppEnv;

    // ЛОГ для дебага
    console.log("[useInstagramIAB]", {
      ua,
      ref,
      forcedEnv,
      isInstagramUA,
      isFacebookIABUA,
      isIGRef,
      isFBRef,
      isTelegramUA,
      hasTelegramWebApp,
      isWhatsAppUA,
      isWhatsAppRef,
      isInstagramIAB,
      isTelegramEnv,
      isWhatsAppEnv,
      isInApp,
    });

    setEnv({
      isInstagramIAB,
      isTelegramEnv,
      isInApp,
      isInstagramUA,
      isFacebookIABUA,
      isIGRef,
      isFBRef,
      isTelegramUA,
      hasTelegramWebApp,
      isWhatsAppUA,
      isWhatsAppRef,
      isWhatsAppEnv,
      forcedEnv,
    });
  }, []);

  return env;
}
